import { FixedIncomePositionEntity } from '@genial/services';
import {
  FixedIncomeOfferBaseEntity,
  FixedIncomeRequestsFixedIncomeOfferCategoriesResponse,
  MetaFilterResponseFixedIcomeFiltersOffersFilters,
  ResponsesFixedIncomeOfferResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import { FilterModalTypes } from '../../../components/FilterModal';
import { metaFilterToOptions } from '../../../utils/metaFilterToOptions';
import {
  DynamicCustomFiltersProps,
  FixedIncomeDistributionFee,
  FixedIncomeOfferEntityNormalized,
  FixedIncomeOfferSellEntityNormalized,
} from '../types';

import { filterHasOptions, getProductCaptions, normalizeCustomFilter } from '.';

export const getRiskRatingLevelValue = (value: number) => {
  const intervals: { [key: string]: number } = {
    '0 - 1.5': 1,
    '1.5 - 2.9': 2,
    '2.9 - 4': 3,
    '4 - 5.5': 4,
    '5.5 - ∞': 5,
  };

  for (const interval in intervals) {
    const [min, max] = interval.split(' - ').map(Number);
    if (value > min && value <= max) {
      return intervals[interval];
    }
  }

  return 0;
};

export const formatDaysRemaining = (date: Date) => {
  const daysRemaining = Utils.Date.DaysDifferenceFromToday(date);
  return `(${daysRemaining} ${daysRemaining > 1 ? 'dias' : 'dia'})`;
};

export const normalizeFixedIncomeDistributionFees = (
  data: FixedIncomeOfferBaseEntity
): FixedIncomeDistributionFee[] => {
  return (
    data.distributionFees?.map((fee) => ({
      ...fee,
      label: fee.profitabilityFormatted || `${Utils.Mask.Percentage(fee.customerFeeAmount)}`,
      customerFeeAmountFormatted: Utils.Mask.Percentage(fee.customerFeeAmount),
      customerPuAmountFormatted: Utils.Mask.Money(fee.customerPuAmount, {
        maximumFractionDigits: 8,
      }),
      min: fee.applicationIncreaseAmount || data.applicationMinimumAmount || 0,
      max: fee.applicationMaximumAmount || data.applicationMaximumAmount || Number.MAX_SAFE_INTEGER,
      profitability: fee.profitabilityFormatted || data.profitabilityFormatted || '',
      honorariumFee: Utils.Mask.Percentage(fee.commissionFee) || '--',
    })) || []
  ).filter((fee) => !!fee.feeId && !!fee.distributionId);
};

export const normalizeFixedIncomeOffers = (
  data?: ResponsesFixedIncomeOfferResponse[] | null
): FixedIncomeOfferEntityNormalized[] => {
  if (!data) {
    return [];
  }

  return data.map(({ offer }) => {
    const normalizedDistributionFees = normalizeFixedIncomeDistributionFees(offer);
    const firstFee = normalizedDistributionFees[0];
    return {
      ...offer,
      productIdForTerm: parseInt(offer.internalProductId || '0', 10),
      // Setting defaults as the first fee in case some values are missing
      feeId: offer.feeId || firstFee?.feeId,
      distributionId: offer.distributionId || firstFee?.distributionId,
      applicationMinimumAmount: offer.applicationMinimumAmount || firstFee?.min,
      applicationMaximumAmount: offer.applicationMaximumAmount || firstFee?.max,
      unitPrice: offer.unitPrice || firstFee?.customerPuAmount,
      profitabilityDaily: offer.profitabilityDaily || firstFee?.profitabilityDaily,
      profitabilityYearly: offer.profitabilityYearly || firstFee?.profitabilityYearly,
      profitabilityToMaturity: offer.profitabilityToMaturity || firstFee?.profitabilityToMaturity,

      productDisplayNormalized: offer.productDisplay || '',
      profitabilityFormattedNormalized:
        offer.profitabilityFormatted || firstFee?.profitabilityFormatted || '',
      suitabilityProfileNameNormalized: offer.suitabilityProfileName || '',
      riskRatingNormalized: offer.riskRating || '-',
      productIdNormalized: offer.productId?.toString() || '',
      maturityDateNormalized: offer.maturityDate
        ? Utils.Mask.DateFormat(offer.maturityDate, 'dd/MM/yyyy')
        : '--',
      daysToMaturityNormalized: offer.maturityDate ? formatDaysRemaining(offer.maturityDate) : '--',
      dueDateNormalized: offer.dueDate ? Utils.Mask.DateFormat(offer.dueDate, 'dd/MM/yyyy') : '--',
      unitPriceNormalized: Utils.Mask.Money(offer.unitPriceAdjusted || firstFee?.customerPuAmount),
      riskRatingLevelNormalized: getRiskRatingLevelValue(offer.riskRatingLevel || 0),
      inputApplicationAmountNormalized: Utils.Mask.Money(
        offer.applicationMinimumAmount || firstFee?.min
      ),
      captions: getProductCaptions(offer),
      incomeTaxAmountNormalized: offer.incomeTaxAmount
        ? Utils.Mask.Percentage(offer.incomeTaxAmount)
        : '--',
      hasFeesOptions: !!normalizedDistributionFees.length,
      distributionFeesNormalized: normalizedDistributionFees,
    };
  });
};

export const normalizeFixedIncomeFilters = (
  data?: FixedIncomeRequestsFixedIncomeOfferCategoriesResponse[] | null
): DynamicCustomFiltersProps => {
  if (!data) {
    return { filters: [] };
  }

  const subCategoryFilter = normalizeCustomFilter('productSubCategory', 'Tipos de produtos', data);

  const filtersWithValues = [subCategoryFilter].filter(filterHasOptions);

  return { filters: filtersWithValues };
};

export const normalizeFixedIncomeFiltersFromMeta = (
  meta: MetaFilterResponseFixedIcomeFiltersOffersFilters
): DynamicCustomFiltersProps['filters'] => {
  if (!meta) {
    return [];
  }

  const categoryTypes = metaFilterToOptions(meta.filter.categoryTypes);
  const productNames = metaFilterToOptions(meta.filter.productNames);

  const subCategoryFilter = normalizeCustomFilter(
    'productSubCategory',
    'Tipos de produtos',
    categoryTypes
  );

  const subCategoryFilterWithExtraProps = {
    ...subCategoryFilter,
    extraProps: {
      hidden: true,
    } as FilterModalTypes.FilterType,
  };

  const productNameFilter = normalizeCustomFilter('productName', 'Tipo de produto', productNames);

  const productNameFilterWithExtraProps = {
    ...productNameFilter,
    extraProps: {
      optionsColumns: 1,
      optionWidth: 'full',
      withScrollableContainer: true,
      scrollablePlaceholder: 'Selecione um produto',
    } as FilterModalTypes.FilterType,
  };

  const filtersWithValues = [
    subCategoryFilterWithExtraProps,
    productNameFilterWithExtraProps,
  ].filter(filterHasOptions);

  return filtersWithValues;
};

export const formatAvailableAmount = (value: number) => {
  const valueNormalized = value || 0;
  return `${String(valueNormalized).replace('.', ',')} ${
    valueNormalized === 1 ? 'unidade' : 'unidades'
  }`;
};

export const normalizeFixedIncomeSellOffers = (
  data?: FixedIncomePositionEntity[] | null
): FixedIncomeOfferSellEntityNormalized[] => {
  if (!data) {
    return [];
  }

  return data.map((fixedIncome) => {
    return {
      ...fixedIncome,
      irFormatted: Utils.Mask.Money(fixedIncome.ir || 0),
      irNormalized: fixedIncome.ir || 0,
      netValueFormatted: Utils.Mask.Money(fixedIncome.netFinancialValue || 0),
      availableQuantityNormalized: formatAvailableAmount(fixedIncome.availableAmount),
      productIdNormalized: fixedIncome.productId?.toString() || '',
      unitaryPriceNormalized:
        fixedIncome.currentUnitaryPrice || fixedIncome.currentMarketUnitaryPrice || 0,
      availableInAccountNormalized: fixedIncome?.redemptionDate
        ? Utils.Mask.DateFormat(fixedIncome?.redemptionDate, 'dd/MM/yyyy')
        : '--',
    };
  });
};
